// MerchantLedger — full ledger management for a single merchant.
//
// Two routes render this page:
//   /merchants/:id/ledger — drill-down from a merchant (id can be the int
//     or the slug; backend resolves either via payouts-backend#8).
//   /ledger — sidebar entry. No :id in the URL, so the merchant comes from
//     the header switcher context (ui.currentMerchantId), the same context
//     that drives X-Merchant-ID on every other id-less screen.
//
// The page renders for any member (viewer+ can read entries), but the
// LedgerEntryForm is only mounted when the caller is admin so non-admins
// don't see a button that would 403.
//
// MerchantDetail also renders a small preview slice that links here; this
// page is the full, uncapped view (backend caps at 500 rows — enough for
// the foreseeable future).
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { LedgerEntryRow } from "@/components/Ledger/LedgerEntryRow";
import { LedgerEntryForm } from "@/components/Ledger/LedgerEntryForm";
import { useAuth, isAdminRole, isSuperadminRole } from "@/store/auth";
import { useUI } from "@/store/ui";
import { useMerchant } from "@/queries/useMerchant";
import { listLedgerEntries } from "@/api/merchantLedger";
import { fmtCcyMinor } from "@/utils/format";

export function MerchantLedger() {
  const { id: idOrSlug } = useParams<{ id: string }>();
  const isAdmin = useAuth((s) => isAdminRole(s.user?.role));
  // Only a superadmin can change the balance source, so only they get the
  // link to it — see isSuperadminRole.
  const isSuperadmin = useAuth((s) => isSuperadminRole(s.user?.role));
  const setCurrentMerchant = useUI((s) => s.setCurrentMerchant);
  const currentMerchantId = useUI((s) => s.currentMerchantId);
  const qc = useQueryClient();

  // Standalone /ledger (sidebar) has no :id — fall back to the merchant
  // the header switcher has selected. On /merchants/:id/ledger the URL
  // wins so an admin can drill into a merchant they aren't "in".
  const standalone = idOrSlug == null;
  const target = standalone
    ? currentMerchantId != null
      ? String(currentMerchantId)
      : undefined
    : idOrSlug;

  // Resolve the merchant first (the URL :id can be either an int or a
  // slug, and downstream queries key on the int id).
  const {
    data: merchant,
    isLoading: merchantLoading,
    isError: merchantError,
  } = useMerchant(target);
  const merchantId = merchant?.id;

  // Sync the global merchant context so the sidebar + header switcher
  // reflect the merchant we're drilled into (admins drilling into a
  // merchant from /merchants should land here with the correct context).
  useEffect(() => {
    if (merchantId != null) {
      setCurrentMerchant(merchantId, merchant?.slug ?? null);
    }
  }, [merchantId, merchant?.slug, setCurrentMerchant]);

  const ledgerQuery = useQuery({
    queryKey: ["merchant-ledger", merchantId],
    queryFn: () => listLedgerEntries(merchantId!),
    enabled: merchantId != null,
    staleTime: 60_000,
  });
  const entries = ledgerQuery.data ?? [];
  // BigInt accumulator — entries.amount_minor can be number | bigint.
  const entrySum = entries.reduce(
    (acc, e) => acc + BigInt(e.amount_minor),
    0n,
  );

  // Sidebar route with nothing selected yet — the switcher is the fix,
  // not a 404. Admins land here before picking a merchant; a merchant
  // user gets a default picked from /v1/me memberships on first login,
  // so in practice they never see this.
  if (target == null) {
    return (
      <div className="page">
        <h1>Ledger</h1>
        <p className="dim">
          Pick a merchant in the switcher at the top of the page to see its
          ledger.
        </p>
      </div>
    );
  }

  if (merchantLoading) {
    return (
      <div className="page">
        <h1>Ledger</h1>
        <p className="dim">Loading merchant…</p>
      </div>
    );
  }

  if (merchantError || !merchant) {
    return (
      <div className="page">
        {!standalone && (
          <Link to="/merchants" className="btn ghost sm">
            <I.ChevronLeft size={12} /> Back to merchants
          </Link>
        )}
        <h1 style={{ marginTop: 16 }}>Merchant not found</h1>
        <p className="dim">
          No merchant with id {target}, or you don't have access to it.
        </p>
      </div>
    );
  }

  const backHref = `/merchants/${merchant.slug || merchant.id}`;

  return (
    <div className="page">
      <div className="page-head" style={{ marginBottom: 16 }}>
        {!standalone && (
          <Link
            to={backHref}
            className="btn ghost sm"
            style={{ marginBottom: 8 }}
          >
            <I.ChevronLeft size={12} /> Back to {merchant.display_name}
          </Link>
        )}
        <div className="row" style={{ alignItems: "baseline", gap: 12 }}>
          <h1 style={{ margin: 0 }}>Ledger</h1>
          <span
            className="dim"
            style={{ fontSize: 13 }}
            title={`Merchant id: ${merchant.id}`}
          >
            {merchant.display_name}
            <span className="mono" style={{ marginLeft: 6 }}>
              ({merchant.slug})
            </span>
          </span>
          <div className="flex-1" />
          <span className="dim mono" style={{ fontSize: 12 }}>
            Σ entries: {fmtCcyMinor(entrySum, "INR")}
          </span>
        </div>
        {/* The ledger accrues for every merchant now (migration 042):
            payouts and fees are entries, whatever the balance card reads
            from. So this is no longer "your entries are inert" — it's
            "the number on their dashboard comes from somewhere else". */}
        {merchant.balance_display && merchant.balance_display !== "ledger" && (
          <div style={{ fontSize: 12, marginTop: 6, color: "var(--ink-3)" }}>
            Every payout and fee is recorded here. This merchant's dashboard
            balance, though, comes from{" "}
            {merchant.balance_display === "hidden"
              ? "nowhere — their balance card is hidden"
              : "their live bank balance"}
            , not from this ledger.{" "}
            {isSuperadmin && (
              <Link to={`${backHref}?edit=1`}>Change the balance source</Link>
            )}
          </div>
        )}
      </div>

      {isAdmin && merchantId != null && (
        <section className="card" style={{ marginBottom: 16 }}>
          <header
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--hair)",
            }}
          >
            <div className="eyebrow">Add entry</div>
          </header>
          <LedgerEntryForm
            merchantId={merchantId}
            onCreated={() =>
              qc.invalidateQueries({
                queryKey: ["merchant-ledger", merchantId],
              })
            }
          />
        </section>
      )}

      <section className="card">
        <header
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid var(--hair)",
          }}
        >
          <div className="eyebrow">History · newest first</div>
        </header>
        <div style={{ padding: "12px 16px" }}>
          {ledgerQuery.isLoading ? (
            <div className="dim" style={{ fontSize: 12 }}>
              Loading entries…
            </div>
          ) : ledgerQuery.isError ? (
            <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
              Could not load ledger entries.
            </div>
          ) : entries.length === 0 ? (
            <div className="dim" style={{ fontSize: 12 }}>
              No ledger entries yet.
              {merchant.balance_display === "ledger" &&
                " The dashboard balance card will show “not initialized” until an entry lands."}
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Kind</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <LedgerEntryRow key={e.id} entry={e} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
